import React, { useState, useRef, useEffect } from "react";
import styles from "./Scoreboard.module.css";
import { useNavigate, useLocation } from "react-router-dom";

import { MatchData } from "../App";
import { Grid, useMediaQuery } from "@mui/material";
import { Colours, SamaggiTypography } from "../Components/SamaggiTypography";
import SamaggiPaper from "../Components/SamaggiPaper";
import { SamaggiButton } from "../Components/SamaggiButton";
import theme from "../theme";


interface ScoreboardProps {
	matches: MatchData[];
	elapsedTime: number;
}

const Scoreboard: React.FC<ScoreboardProps> = ({ matches }) => {
	const [selectedMatch, setSelectedMatch] = useState<number>(0);
	const [showAllMatches, setShowAllMatches] = useState(false);
	const navigate = useNavigate();
	const [sportFilter, setSportFilter] = useState<string>("");
	const [categoryFilter, setCategoryFilter] = useState<string>("");
	const [timeFilter, setTimeFilter] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [knockoutStageFilter, setKnockoutStageFilter] = useState<string>("");
	const [quarterOrSet, setQuarterOrSet] = useState(matches[selectedMatch]?.quarterOrSet ?? 0);
	const [fouls1, setFouls1] = useState(matches[selectedMatch]?.fouls1 ?? 0);
	const [fouls2, setFouls2] = useState(matches[selectedMatch]?.fouls2 ?? 0);
	const [elapsedTime, setElapsedTime] = useState(0); // Starts from 0 seconds
	const [isRunning, setIsRunning] = useState(false);
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const isFinalStageMatch = (match: MatchData) => {
		const stageText = `${match.group || ""} ${match.stage || ""}`.toLowerCase();
		return stageText.includes("quarter") || stageText.includes("semi") || stageText.includes("grand");
	};

	const filteredMatches = matches.filter((match) => {
		const sportOk = !sportFilter || match.sport.toLowerCase().includes(sportFilter.toLowerCase());
		const categoryOk = !categoryFilter || match.sport.includes(categoryFilter);
		const statusOk = !statusFilter || match.status.includes(statusFilter);
		const stageText = `${match.group || ""} ${match.stage || ""}`.toLowerCase();
		const stageOk = !knockoutStageFilter || stageText.includes(knockoutStageFilter);
		return sportOk && categoryOk && statusOk && stageOk && shouldShowSet(match.match_id);
	});

	const [badmintonSets, setBadmintonSets] = useState<MatchData[]>([]);


	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const matchIdFromURL = searchParams.get("match");
	const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
	const isMedScreen = useMediaQuery(theme.breakpoints.down("md"));

	useEffect(() => {
		if (matchIdFromURL) {
			const matchFromProps = matches.find((match) => match.match_id === matchIdFromURL);
			if (matchFromProps && matchFromProps.match_id !== matches[selectedMatch]?.match_id) {
				setSelectedMatch(matches.indexOf(matchFromProps));
			}
		}
	}, [matches, location.search]); // Keep in sync with URL changes

	const handleMatchSelection = (match: MatchData) => {
		console.log("🔹 handleMatchSelection called with match:", match);
		window.scrollTo({ top: 0, behavior: "auto" });

		// Find the index of the match in the matches array
		const matchIndex = matches.findIndex(m => m.match_id === match.match_id);

		if (matchIndex !== -1) {
			setSelectedMatch(matchIndex);
		}

		// Update URL
		const searchParams = new URLSearchParams(location.search);
		searchParams.set("match", match.match_id);
		navigate(`?${searchParams.toString()}`, { replace: true });

		// If it's badminton, extract sets
		if (match.sport.startsWith("Badminton") && match.match_id.length > 36) {
			const baseId = match.match_id.slice(0, 36);
			const allSets = matches.filter(m => m.match_id.startsWith(baseId));

			console.log("🔹 Available Sets for Badminton:", allSets);
			setBadmintonSets(allSets);

			// Automatically select set `-1` when first clicked
			const firstSet = allSets.find(m => m.match_id.endsWith("-1"));
			if (firstSet) {
				setSelectedMatch(matches.indexOf(firstSet));
			}
		} else {
			setBadmintonSets([]);
		}
	};

	useEffect(() => {
		if (matches.length > 0 && selectedMatch !== null) {
			const selected = matches[selectedMatch];

			if (selected?.sport.startsWith("Badminton") && selected.match_id.length > 36) {
				const baseId = selected.match_id.slice(0, 36);
				const allSets = matches.filter(m => m.match_id.startsWith(baseId));

				setBadmintonSets(allSets);
			} else {
				setBadmintonSets([]);
			}
		}
	}, [selectedMatch, matches]);

	const updateSet = (newSet: number) => {
		if (!matches[selectedMatch]) return;

		const selectedMatchObj = matches[selectedMatch];

		if (selectedMatchObj.match_id.length > 36) {
			const baseId = selectedMatchObj.match_id.slice(0, 36);
			const newSetMatch = matches.find(m =>
				m.match_id.startsWith(baseId) && m.match_id.endsWith(`-${newSet}`)
			);

			if (newSetMatch) {
				const newSetIndex = matches.indexOf(newSetMatch);
				if (newSetIndex !== -1) {
					setSelectedMatch(newSetIndex);
				}

				// Update URL
				const searchParams = new URLSearchParams(location.search);
				searchParams.set("match", newSetMatch.match_id);
				navigate(`?${searchParams.toString()}`, { replace: true });
			} else {
				console.warn("⚠️ No match found for set", newSet);
			}
		}
	};

	function numToShow() {
		if (isMedScreen) {
			return 4
		}
		if (isLargeScreen) {
			return 3
		}
		return 4
	}

	function shouldShowSet(matchId: string) {
		if (matchId.length <= 42) return true;

		const baseId = matchId.slice(0, 42);
		const suffix = matchId.slice(42);

		// Validate suffix format (-number)
		const suffixMatch = suffix.match(/^-(\d+)$/);
		// console.log(suffixMatch)
		return (!suffixMatch || suffixMatch[1] === "1");
	}

	function findHighestActiveSet(
		matchId: string
	): { index: number; score: [number, number] } | null {
		// Check if match ID has a multi-set format
		if (matchId.length <= 42) return null;

		// Extract base ID (first 36 characters) and potential set suffix
		const baseId = matchId.slice(0, 42);
		const suffix = matchId.slice(42);

		// Validate suffix format (-number)
		const suffixMatch = suffix.match(/^-(\d+)$/);
		if (!suffixMatch) return null;

		console.log("FOUND MORE")

		// Find all related sets
		const related = matches.filter(m =>
			m.match_id.startsWith(baseId) &&
			m.match_id.length > 42 &&
			/^-\d+$/.test(m.match_id.slice(42))
		);

		console.log(related)

		// Extract and sort set numbers
		const sets = related
			.map(m => ({
				set: parseInt(m.match_id.slice(42), 10),
				match: m
			}))
			.sort((a, b) => b.set - a.set);

		// Find highest active set
		for (const { set, match } of sets) {
			if ((match.score1 === 0 && match.score2 === 0) ||
				match.status === "Playing") {
				return {
					index: set * (-1),
					score: [match.score1, match.score2]
				};
			}
		}

		return null;
	}




	return (
		<div style={{ paddingLeft: !isLargeScreen ? "20%" : "25px", paddingRight: !isLargeScreen ? "20%" : "25px", paddingTop: "40px", paddingBottom: "40px" }}>
			<Grid container direction={"column"} rowSpacing={3}>
				<Grid item>
					<SamaggiTypography xlarge bold colour={Colours.samaggiGreen}>Samaggi Live Scoreboard</SamaggiTypography>
				</Grid>


				{/* main match display */}
				<Grid item>
					<SamaggiPaper maxWidth wide noMargin colour={"rgb(0, 41, 44)"}>
						<Grid container direction={"column"} spacing={6}>
							<Grid item>
								<SamaggiTypography colour={Colours.white} center xlarge bold>{matches[selectedMatch] ? matches[selectedMatch].sport.toUpperCase() : "Select a Match"} </SamaggiTypography>
							</Grid>

							{matches[selectedMatch]?.sport.startsWith("Badminton") && badmintonSets.length > 0 && (
								<Grid item container direction={"column"} rowSpacing={2}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center bold large>Select Set:</SamaggiTypography>
									</Grid>
									<Grid item container direction="row" spacing={2} justifyContent="center">
										{badmintonSets
											.slice()
											.sort((a, b) => {
												const setA = parseInt(a.match_id.split("-").pop() || "0");
												const setB = parseInt(b.match_id.split("-").pop() || "0");
												return setA - setB; // Ensures ascending order
											})
											.map((setMatch) => (
												<Grid item key={setMatch.match_id}>
													<SamaggiButton
														onClick={() => updateSet(parseInt(setMatch.match_id.split("-").pop() || "1"))}
														colour={setMatch.match_id === matches[selectedMatch]?.match_id ? Colours.grey : Colours.samaggiGreen}
													>
														Set {setMatch.match_id.split("-").pop()}
													</SamaggiButton>
												</Grid>
											))}
									</Grid>
								</Grid>
							)}


							<Grid item container direction={"row"}>
								<Grid item container direction={"column"} xs={12} md={5}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large bold>{matches[selectedMatch] ? matches[selectedMatch].team1Name : "Team A"}</SamaggiTypography>
									</Grid>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large bold>{matches[selectedMatch] ? matches[selectedMatch].score1 : "0"}</SamaggiTypography>
									</Grid>
								</Grid>
								<Grid item xs={12} md={2}>
									<SamaggiTypography colour={Colours.white} center large>V</SamaggiTypography>
								</Grid>
								<Grid item container direction={"column"} xs={12} md={5}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large bold>{matches[selectedMatch] ? matches[selectedMatch].team2Name : "Team B"}</SamaggiTypography>
									</Grid>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center large bold>{matches[selectedMatch] ? matches[selectedMatch].score2 : "0"}</SamaggiTypography>
									</Grid>
								</Grid>
							</Grid>

							{/* status and timer */}
							<Grid item container direction={"row"} rowSpacing={3}>
								{/* <div className={styles.timerContainer}>
							<span className={styles.detailLabel}>Timer</span>
							<span className={styles.detailValue}>{formatTime(elapsedTime)}</span>
						</div> */}
								{/* Display Current Set for Badminton Matches */}

								{matches[selectedMatch]?.sport.startsWith("Badminton") && (
									<Grid item container direction={"column"} xs={6} lg={3}>
										<Grid item>
											<SamaggiTypography colour={Colours.white} bold center>Current Set</SamaggiTypography>
										</Grid>
										<Grid item>
											<SamaggiTypography colour={Colours.white} center>
												{matches[selectedMatch] ? matches[selectedMatch].match_id.split("-").pop() : "1"}
											</SamaggiTypography>
										</Grid>
									</Grid>
								)}


								{/* Quarter/Set with Buttons */}
								{!matches[selectedMatch]?.sport.startsWith("Badminton") && (
									<Grid item container direction={"column"} xs={6} lg={3}>
										<Grid item>
											<SamaggiTypography colour={Colours.white} bold center>Quarter/Set</SamaggiTypography>
										</Grid>
										<Grid item>
											<SamaggiTypography colour={Colours.white} center>{matches[selectedMatch] ? matches[selectedMatch].quarterOrSet : "1"}</SamaggiTypography>
										</Grid>
									</Grid>
								)}

								{/* Fouls for Team 1 */}
								<Grid item container direction={"column"} xs={6} lg={3}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} bold center>Fouls ({matches[selectedMatch] ? matches[selectedMatch]?.team1Name : "Team A"})</SamaggiTypography>
									</Grid>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center>{matches[selectedMatch] ? matches[selectedMatch].fouls1 : "0"}</SamaggiTypography>
									</Grid>
								</Grid>

								{/* Fouls for Team 2 */}
								<Grid item container direction={"column"} xs={6} lg={3}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} bold center>Fouls ({matches[selectedMatch] ? matches[selectedMatch]?.team2Name : "Team B"})</SamaggiTypography>
									</Grid>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center>{matches[selectedMatch] ? matches[selectedMatch].fouls2 : "0"}</SamaggiTypography>
									</Grid>
								</Grid>
								<Grid item container direction={"column"} xs={6} lg={3}>
									<Grid item>
										<SamaggiTypography colour={Colours.white} bold center>Status</SamaggiTypography>
									</Grid>
									<Grid item>
										<SamaggiTypography colour={Colours.white} center>{matches[selectedMatch] ? matches[selectedMatch].status : "N/A"}</SamaggiTypography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</SamaggiPaper>
				</Grid>

				{/* filter part*/}
				<Grid item container direction={"row"} rowSpacing={3}>
					<Grid item lg={6} xs={12}>
						<SamaggiTypography large bold>Filter Matches</SamaggiTypography>
					</Grid>
					<Grid item lg={6} xs={12}>
						<div className={styles.filterGroup}>
							{/* sports filter */}
							<select
								className={styles.dropdown}
								value={sportFilter}
								onChange={(e) => setSportFilter(e.target.value)}
							>
								<option value="">All Sports</option>
								<option value="Badminton">Badminton</option>
								<option value="Basketball">Basketball</option>
								<option value="Chairball">Chairball</option>
								<option value="Football">Football</option>
								<option value="Volleyball">Volleyball</option>
							</select>

							{/* category filter */}
							<select
								className={styles.dropdown}
								value={categoryFilter}
								onChange={(e) => setCategoryFilter(e.target.value)}
							>
								<option value="">All Categories</option>
								<option value="Male">Male</option>
								<option value="Female">Female</option>
								<option value="Mixed">Mixed</option>
							</select>

							{/* time filter */}
							<select
								className={styles.dropdown}
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
							>
								<option value="">All Statuses</option>
								<option value="Not Started">Not Started</option>
								<option value="Playing">Playing</option>
								<option value="Paused">Paused</option>
								<option value="Ended">Ended</option>
							</select>

							<select
								className={styles.dropdown}
								value={knockoutStageFilter}
								onChange={(e) => setKnockoutStageFilter(e.target.value)}
							>
								<option value="">All Stages</option>
								<option value="quarter">Quarter</option>
								<option value="semi">Semi</option>
								<option value="grand">Grand</option>
								<option value="thirdplace">Third-Place</option>
							</select>
						</div>
					</Grid>
				</Grid>
				{/* Matches List */}
				<Grid item container direction={"column"} rowSpacing={4} sx={{ marginTop: 0 }}>
					<Grid item>
						<SamaggiTypography bold large center>ALL MATCHES</SamaggiTypography>
					</Grid>
					<Grid item container direction={"row"} columnSpacing={2} rowSpacing={2}>
						{filteredMatches
							.slice(0, showAllMatches ? filteredMatches.length : numToShow())
							.map((match, index) => (
								<>
									<Grid item key={match.match_id} onClick={() => handleMatchSelection(match)} lg={3} md={4} xs={6}>
										<SamaggiPaper noPad noMargin>
											<div style={{ padding: "10px", paddingTop: "20px" }}>
												<Grid container direction={"column"} rowSpacing={3}>
													<Grid item container direction={"column"} spacing={1}>
														<Grid item>
															<SamaggiTypography center bold={match.status === "Playing" || match.status === "Soon"} colour={match.status === "Playing" ? Colours.green : ((match.status === "Not Started" || match.status === "Ended") ? Colours.black : Colours.red)}>{match.status}</SamaggiTypography>
														</Grid>
														<Grid item>
															<SamaggiTypography bold center large>{match.sport}</SamaggiTypography>
														</Grid>
													</Grid>

													<Grid item container direction={"column"}>
														<Grid item>
															<SamaggiTypography bold center>{match.team1Name}</SamaggiTypography>
														</Grid>
														<Grid item>
															<SamaggiTypography center> vs </SamaggiTypography>
														</Grid>
														<Grid item>
															<SamaggiTypography bold center>{match.team2Name}</SamaggiTypography>
														</Grid>
													</Grid>
													{findHighestActiveSet(match.match_id) === null && <Grid item container direction={"column"}>
														<Grid item>
															<SamaggiTypography bold center>Live Score</SamaggiTypography>
														</Grid>
														<Grid item>
															<SamaggiTypography center bold xlarge>{match.score1} - {match.score2}</SamaggiTypography>
														</Grid>
													</Grid>}
													{findHighestActiveSet(match.match_id) !== null && <Grid item container direction={"column"}>
														<Grid item>
															<SamaggiTypography bold center>{`Set ${findHighestActiveSet(match.match_id)?.index} Score`}</SamaggiTypography>
														</Grid>
														<Grid item>
															<SamaggiTypography center bold xlarge>{findHighestActiveSet(match.match_id)?.score[0]} - {findHighestActiveSet(match.match_id)?.score[1]}</SamaggiTypography>
														</Grid>
													</Grid>}
													<Grid item container direction={"column"} rowSpacing={0}>
														<Grid item>
															<SamaggiTypography large bold center>{match.startTime}</SamaggiTypography>
														</Grid>
														<Grid item>
															<SamaggiTypography center>{match.location}</SamaggiTypography>
														</Grid>
													</Grid>
													<Grid item>
														<SamaggiButton fullWidth onClick={(e) => { e.stopPropagation(); handleMatchSelection(match); }}>View</SamaggiButton>
													</Grid>
												</Grid>
											</div>
										</SamaggiPaper>
									</Grid>
								</>
							))}
					</Grid>

					<Grid item style={{ display: "flex", justifyContent: "center", alignItems: "center" }} xs={12}>
						<SamaggiButton onClick={() => setShowAllMatches(!showAllMatches)} paddingX={"20px"} paddingY={"10px"}>
							{showAllMatches ? "Hide Matches" : "View All Matches"}
						</SamaggiButton>
					</Grid>
				</Grid>

				{/*/!* Leaderboard *!/*/}
				{/*<section className={styles.leaderboardSection}>*/}
				{/*	<h3 className={styles.leaderboardTitle}>LEADERBOARD</h3>*/}
				{/*	<ol className={styles.leaderboardList}>*/}
				{/*		<li className={styles.leaderboardItem}>*/}
				{/*			<span className={styles.position}>1</span>*/}
				{/*			<span className={styles.uniName}>Uni A</span>*/}
				{/*			<span className={styles.points}>50 pts</span>*/}
				{/*		</li>*/}
				{/*		<li className={styles.leaderboardItem}>*/}
				{/*			<span className={styles.position}>2</span>*/}
				{/*			<span className={styles.uniName}>Uni B</span>*/}
				{/*			<span className={styles.points}>47 pts</span>*/}
				{/*		</li>*/}
				{/*		<li className={styles.leaderboardItem}>*/}
				{/*			<span className={styles.position}>3</span>*/}
				{/*			<span className={styles.uniName}>Uni C</span>*/}
				{/*			<span className={styles.points}>45 pts</span>*/}
				{/*		</li>*/}
				{/*	</ol>*/}
				{/*	<button className={styles.viewLink} onClick={() => navigate("/ranking")}>View Full</button>*/}
				{/*</section>*/}
			</Grid>
		</div>
	);
};

export default Scoreboard;

// /** 2) Main Scoreboard component. */
// const Scoreboard: React.FC = () => {
//   // 2a) Put all your matches in an array of objects
//   const allMatches: MatchData[] = [
//     {
//       sport: "Basketball",
//       team1Name: "Team 1",
//       team2Name: "Team 2",
//       score1: 1,
//       score2: 2,
//       timer: "8:23",
//       quarterOrSet: 2,
//       fouls1: 2,
//       fouls2: 3,
//       location: "Main Court",
//       status: "CURRENT",
//     },
//     {
//       sport: "Volleyball",
//       team1Name: "Team A",
//       team2Name: "Team B",
//       score1: 18,
//       score2: 25,
//       timer: "00:00",
//       quarterOrSet: 3, // might represent "Set #3"
//       fouls1: 1,
//       fouls2: 2,
//       location: "Court 1",
//       status: "CURRENT",
//     },
//     {
//       sport: "Football",
//       team1Name: "Team A",
//       team2Name: "Team B",
//       score1: 2,
//       score2: 2,
//       timer: "30:15",
//       quarterOrSet: 2, // might represent "Half #2"
//       fouls1: 4,
//       fouls2: 5,
//       location: "Field 2",
//       status: "CURRENT",
//     },
//     {
//       sport: "Badminton",
//       team1Name: "Team A",
//       team2Name: "Team B",
//       score1: 0,
//       score2: 0,
//       timer: "Not started",
//       quarterOrSet: 1,
//       fouls1: 0,
//       fouls2: 0,
//       location: "Hall 3",
//       status: "10:45",   // Start time
//     },
//     {
//       sport: "Chairball", // Chairball match
//       team1Name: "Team X",
//       team2Name: "Team Y",
//       score1: 3,
//       score2: 4,
//       timer: "12:45",
//       quarterOrSet: 2,
//       fouls1: 2,
//       fouls2: 1,
//       location: "Stadium 2",
//       status: "CURRENT",
//     }
//   ];

//   /** 2b) Create state for the currently selected match (the one displayed on top). */
//   const [selectedMatch, setSelectedMatch] = useState<MatchData>(allMatches[0]);

//   /** 2c) Function to handle clicking "view" on a match card. */
//   const handleViewMatch = (match: MatchData) => {
//     // Update the selected match so it appears on top scoreboard
//     setSelectedMatch(match);
//   };

//   const navigate = useNavigate();

//   const handleClick = () => {
//     // do something...
//     navigate("/ranking");
//   };

//   return (
//     <div className={styles.scoreboardContainer}>

//       {/* 3) TOP SCOREBOARD SECTION - shows the 'selectedMatch' data */}
//       <section className={styles.currentMatchSection}>
//         <h2 className={styles.currentSportTitle}>
//           {selectedMatch.sport.toUpperCase()}
//         </h2>

//         <div className={styles.teamsContainer}>
//           <div className={styles.teamBlock}>
//             <span className={styles.teamName}>{selectedMatch.team1Name}</span>
//             <span className={styles.teamScore}>{selectedMatch.score1}</span>
//           </div>
//           <span className={styles.vs}>V</span>
//           <div className={styles.teamBlock}>
//             <span className={styles.teamName}>{selectedMatch.team2Name}</span>
//             <span className={styles.teamScore}>{selectedMatch.score2}</span>
//           </div>
//         </div>

//         <div className={styles.matchDetails}>
//           <div>
//             <span className={styles.detailLabel}>Timer</span>
//             <span className={styles.detailValue}>{selectedMatch.timer}</span>
//           </div>
//           <div>
//             <span className={styles.detailLabel}>Quarter</span>
//             <span className={styles.detailValue}>
//               {selectedMatch.quarterOrSet}
//             </span>
//           </div>
//           <div>
//             <span className={styles.detailLabel}>
//               Fouls ({selectedMatch.team1Name})
//             </span>
//             <span className={styles.detailValue}>{selectedMatch.fouls1}</span>
//           </div>
//           <div>
//             <span className={styles.detailLabel}>
//               Fouls ({selectedMatch.team2Name})
//             </span>
//             <span className={styles.detailValue}>{selectedMatch.fouls2}</span>
//           </div>
//         </div>
//       </section>

//       {/* 4) ALL MATCHES SECTION - when user clicks "view", we call handleViewMatch(match) */}
//       <section className={styles.allMatchesSection}>
//         <h3 className={styles.allMatchesTitle}>ALL MATCHES</h3>
//         <div className={styles.matchesList}>
//           {allMatches.map((match, idx) => (
//             <div key={idx} className={styles.matchCard}>
//               <h4>{match.sport}</h4>
//               <p>
//                 {match.team1Name} vs {match.team2Name}
//               </p>
//               <p>{match.location}</p>
//               <p className={styles.matchStatus}>{match.status}</p>
//               {/* Replace <a> with <button> so we can do an onClick */}
//               <button
//                 className={styles.viewLinkButton}
//                 onClick={() => handleViewMatch(match)}
//               >
//                 view
//               </button>
//             </div>
//           ))}
//         </div>
//         <button className={styles.viewLink} onClick={handleClick}>
//             view full
//         </button>

//       </section>

//       {/* 5) LEADERBOARD (optional, add your own data) */}
//       <section className={styles.leaderboardSection}>
//         <h3 className={styles.leaderboardTitle}>LEADERBOARD</h3>
//         <ol className={styles.leaderboardList}>
//           <li className={styles.leaderboardItem}>
//             <span className={styles.position}>1</span>
//             <span className={styles.uniName}>Uni A</span>
//             <span className={styles.points}>50 pts</span>
//           </li>
//           <li className={styles.leaderboardItem}>
//             <span className={styles.position}>2</span>
//             <span className={styles.uniName}>Uni B</span>
//             <span className={styles.points}>47 pts</span>
//           </li>
//           <li className={styles.leaderboardItem}>
//             <span className={styles.position}>3</span>
//             <span className={styles.uniName}>Uni C</span>
//             <span className={styles.points}>45 pts</span>
//           </li>
//         </ol>
//         <button className={styles.viewLink} onClick={handleClick}>
//             view full
//         </button>
//       </section>
//     </div>
//   );
// };

// export default Scoreboard;
